import { useState, useRef } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import HelicopterBookingForm from "@/components/HelicopterBookingForm";
import HelicopterPackageCard from "@/components/HelicopterPackageCard";
import { HELICOPTER_PACKAGES } from "@/types/helicopter";
import heroImage from "@/assets/helicopter-hero-bg.jpg";
import aboutImage from "@/assets/helicopter-about.jpg";
import gallery1 from "@/assets/helicopter-gallery-1.jpg";
import gallery2 from "@/assets/helicopter-gallery-2.jpg";
import gallery3 from "@/assets/helicopter-gallery-3.jpg";
import gallery4 from "@/assets/helicopter-gallery-4.jpg";
import gallery5 from "@/assets/helicopter-gallery-5.jpg";
import gallery6 from "@/assets/helicopter-gallery-6.jpg";
import paymentMethods from "@/assets/payment-methods.png";
const HelicopterRide = () => {
  const [selectedPackageId, setSelectedPackageId] = useState<string>("");
  const bookingFormRef = useRef<HTMLDivElement>(null);
  const handlePackageSelect = (packageId: string) => {
    setSelectedPackageId(packageId);
    bookingFormRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };
  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="py-3 px-4 w-full bg-transparent absolute top-0 left-0 right-0 z-10">
        <div className="container mx-auto flex items-center justify-center bg-transparent">
          <h1 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">Helicopter Rides in Dubai</h1>
        </div>
      </header>

      {/* Hero Section with Booking Form */}
      <section className="relative min-h-screen flex items-start">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${heroImage})`,
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-16 pt-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <div className="max-w-md" ref={bookingFormRef}>
              <HelicopterBookingForm selectedPackageId={selectedPackageId} />
            </div>
            <div className="max-w-2xl hidden lg:block">
              <div className="bg-background/10 backdrop-blur-sm rounded-lg p-8 text-white">
                <h2 className="text-3xl font-bold mb-4">Experience Dubai From Above</h2>
                <p className="text-lg mb-6 opacity-90">
                  Soar above the iconic skyline and witness the breathtaking beauty of Dubai from an unparalleled
                  vantage point. Our helicopter tours offer unforgettable aerial views of the world's most magnificent
                  landmarks.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>Professional certified pilots</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>State-of-the-art helicopters</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>Panoramic views of iconic landmarks</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="text-primary text-xl">✓</span>
                    <span>Flexible booking options</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 px-4 bg-slate-950">
        <div className="container mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Choose Your Experience</h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Select from our premium helicopter tours and witness Dubai's iconic skyline from above
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {HELICOPTER_PACKAGES.map((pkg) => (
              <HelicopterPackageCard key={pkg.id} package={pkg} onBookNow={handlePackageSelect} />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-24 px-4 bg-slate-900">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url(${heroImage})`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/95 to-slate-900" />
        <div className="relative z-10 container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
            <div className="group">
              <div className="text-6xl md:text-7xl font-bold mb-3 text-gold group-hover:scale-110 transition-transform">
                1000+
              </div>
              <div className="text-lg text-slate-300">Successful Flights</div>
            </div>
            <div className="group">
              <div className="text-6xl md:text-7xl font-bold mb-3 text-gold group-hover:scale-110 transition-transform">
                5000+
              </div>
              <div className="text-lg text-slate-300">Happy Guests</div>
            </div>
            <div className="group">
              <div className="text-6xl md:text-7xl font-bold mb-3 text-gold group-hover:scale-110 transition-transform">
                4.8
              </div>
              <div className="text-lg text-slate-300">Star Reviews</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 bg-slate-950">
        <div className="container mx-auto">
          <p className="text-sm font-medium text-gold uppercase tracking-widest mb-4">
            Why thousands of travelers choose us for Helicopter Tours
          </p>
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-white">
                Your Gateway
                <br />
                <span className="text-gold">to the Sky</span>
              </h2>
              <p className="text-slate-300 leading-relaxed mb-6">
                We are Dubai's premier booking platform dedicated to luxury helicopter experiences. Our aerial tours
                showcase the city's celebrated attractions from a privileged angle, combining thrilling adventure with
                uncompromising safety standards.
              </p>
              <p className="text-slate-200 font-medium mb-4">We take pride in:</p>
              <ul className="space-y-4 text-slate-300">
                <li className="flex items-start gap-3">
                  <span className="text-gold mt-1 text-lg">✓</span>
                  <span>
                    <strong className="text-white">Licensed Operators:</strong> We only work with GCAA-certified pilots
                    and operators.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gold mt-1 text-lg">✓</span>
                  <span>
                    <strong className="text-white">State-of-the-Art Fleet:</strong> Modern helicopters maintained to the
                    highest standards.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gold mt-1 text-lg">✓</span>
                  <span>
                    <strong className="text-white">Instant Confirmation:</strong> Secure your spot immediately with no
                    waiting time.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gold mt-1 text-lg">✓</span>
                  <span>
                    <strong className="text-white">No Hidden Fees:</strong> The price you see is the price you pay.
                  </span>
                </li>
              </ul>
              <p className="text-slate-300 leading-relaxed mt-6">
                When you fly with us, you don't just take a ride – you create memories that last a lifetime.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-gold/20 to-transparent rounded-2xl blur-xl" />
              <img
                src={aboutImage}
                alt="Passengers enjoying helicopter tour over Dubai"
                className="relative rounded-2xl shadow-2xl w-full h-auto border border-slate-700"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 px-4 bg-slate-900">
        <div className="container mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Gallery</h2>
            <p className="text-lg text-slate-400">Stunning views from our helicopter tours</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
            <img
              src={gallery1}
              alt="Helicopter view of Palm Jumeirah Dubai"
              className="rounded-xl w-full h-64 object-cover hover:scale-[1.02] transition-transform duration-500 border border-slate-700/50"
            />
            <img
              src={gallery2}
              alt="Helicopter flying near Burj Khalifa"
              className="rounded-xl w-full h-64 object-cover hover:scale-[1.02] transition-transform duration-500 border border-slate-700/50"
            />
            <img
              src={gallery3}
              alt="Aerial view of Dubai Marina from helicopter"
              className="rounded-xl w-full h-64 object-cover hover:scale-[1.02] transition-transform duration-500 border border-slate-700/50"
            />
            <img
              alt="Helicopter tour over Dubai coastline"
              className="rounded-xl w-full h-64 object-cover hover:scale-[1.02] transition-transform duration-500 border border-slate-700/50"
              src="/lovable-uploads/a232a69d-8f9d-4a28-a475-949b121b0966.png"
            />
            <img
              src={gallery5}
              alt="Inside helicopter cockpit during Dubai tour"
              className="rounded-xl w-full h-64 object-cover hover:scale-[1.02] transition-transform duration-500 border border-slate-700/50"
            />
            <img
              src={gallery6}
              alt="Aerial view of World Islands Dubai"
              className="rounded-xl w-full h-64 object-cover hover:scale-[1.02] transition-transform duration-500 border border-slate-700/50"
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-slate-950">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">FAQs</h2>
            <p className="text-lg text-slate-400">Helicopter Tours</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem
              value="item-1"
              className="border border-slate-700 rounded-xl px-6 bg-slate-900/50 backdrop-blur"
            >
              <AccordionTrigger className="text-left font-semibold text-white hover:no-underline hover:text-gold transition-colors">
                1. Is helicopter touring in Dubai safe?
              </AccordionTrigger>
              <AccordionContent className="text-slate-300">
                Absolutely. We only partner with operators who are licensed by the UAE General Civil Aviation Authority
                (GCAA). All pilots are internationally certified, and each helicopter undergoes rigorous daily
                maintenance checks following strict safety protocols.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-2"
              className="border border-slate-700 rounded-xl px-6 bg-slate-900/50 backdrop-blur"
            >
              <AccordionTrigger className="text-left font-semibold text-white hover:no-underline hover:text-gold transition-colors">
                2. What is the best time to book a helicopter tour?
              </AccordionTrigger>
              <AccordionContent className="text-slate-300">
                Helicopter tours operate throughout the day. Morning flights offer clearer visibility, while sunset
                flights provide stunning golden hour views. We recommend booking 2-3 days in advance during peak season
                (October to March).
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-3"
              className="border border-slate-700 rounded-xl px-6 bg-slate-900/50 backdrop-blur"
            >
              <AccordionTrigger className="text-left font-semibold text-white hover:no-underline hover:text-gold transition-colors">
                3. What is the difference between sharing and private flights?
              </AccordionTrigger>
              <AccordionContent className="text-slate-300">
                Sharing flights allow you to book individual seats alongside other guests, making it more affordable.
                Private flights give you exclusive use of the helicopter for up to 5 passengers, perfect for families or
                groups seeking a personalized experience.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-4"
              className="border border-slate-700 rounded-xl px-6 bg-slate-900/50 backdrop-blur"
            >
              <AccordionTrigger className="text-left font-semibold text-white hover:no-underline hover:text-gold transition-colors">
                4. Can children participate in helicopter tours?
              </AccordionTrigger>
              <AccordionContent className="text-slate-300">
                Yes! Children of all ages are welcome. Children under 2 years old may sit on an adult's lap at no extra
                charge. It's a magical experience for the whole family.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-5"
              className="border border-slate-700 rounded-xl px-6 bg-slate-900/50 backdrop-blur"
            >
              <AccordionTrigger className="text-left font-semibold text-white hover:no-underline hover:text-gold transition-colors">
                5. What happens if the weather is bad?
              </AccordionTrigger>
              <AccordionContent className="text-slate-300">
                Your safety is our priority. If a flight is cancelled due to weather conditions (fog, wind, or
                sandstorms), you will be offered a full refund or the option to reschedule for the next available date
                at no additional cost.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-6"
              className="border border-slate-700 rounded-xl px-6 bg-slate-900/50 backdrop-blur"
            >
              <AccordionTrigger className="text-left font-semibold text-white hover:no-underline hover:text-gold transition-colors">
                6. What should I wear?
              </AccordionTrigger>
              <AccordionContent className="text-slate-300">
                Wear comfortable clothing. Avoid loose scarves or hats that could blow away. Dark-colored clothing is
                recommended to reduce glare on photographs. Closed-toe shoes are preferred.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-7"
              className="border border-slate-700 rounded-xl px-6 bg-slate-900/50 backdrop-blur"
            >
              <AccordionTrigger className="text-left font-semibold text-white hover:no-underline hover:text-gold transition-colors">
                7. Can I bring my camera or phone?
              </AccordionTrigger>
              <AccordionContent className="text-slate-300">
                Absolutely! We encourage you to capture the stunning views. Just ensure your device is secured with a
                strap or wrist lanyard. GoPros and action cameras are also welcome.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-8"
              className="border border-slate-700 rounded-xl px-6 bg-slate-900/50 backdrop-blur"
            >
              <AccordionTrigger className="text-left font-semibold text-white hover:no-underline hover:text-gold transition-colors">
                8. Where does the helicopter tour depart from?
              </AccordionTrigger>
              <AccordionContent className="text-slate-300">
                Tours typically depart from helipads at Atlantis, The Palm or Dubai Festival City. The exact departure
                location will be confirmed upon booking.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="item-9"
              className="border border-slate-700 rounded-xl px-6 bg-slate-900/50 backdrop-blur"
            >
              <AccordionTrigger className="text-left font-semibold text-white hover:no-underline hover:text-gold transition-colors">
                9. What is your cancellation and refund policy?
              </AccordionTrigger>
              <AccordionContent className="text-slate-300">
                Cancellations made 72+ hours before receive a 100% refund. 48-72 hours before receive 50% refund.
                Cancellations within 24 hours are non-refundable. Weather-related cancellations by the operator always
                receive a full refund. info@hotairballoonrideindubai.com
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-12 px-4 bg-slate-900 border-t border-slate-800">
        <div className="container mx-auto text-center">
          <img
            src={paymentMethods}
            alt="Accepted payment methods: Visa, Mastercard, Tabby, Google Pay, Apple Pay"
            className="mx-auto max-w-2xl w-full h-auto opacity-80"
          />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-10 px-4 border-t border-slate-800">
        <div className="container mx-auto text-center">
          <p className="text-sm text-slate-400">©2025 Helicopter Rides Dubai. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
};
export default HelicopterRide;
